import "./multiplayerMenu.scss";
import { useGame } from "../../../context/GameContext";
import { useRef, useEffect } from "react";
import WaitingRoom from "../WaitingRoom/waitingRoom";
import { activeRow, setActiveRow, KeyCodes } from "../../../utils/navigationHelpers";
import ChessButton from "../../visual/ChessButton/ChessButton";
import useSoundEffect from "../../../hooks/useSoundEffect";
import { useMultiplayer } from "../../../context/MultiplayerContext";
import { getSavedLobby } from "../../../utils/multiplayer/savedLobbyManager";

/**
 * The MultiplayerMenu component.
 * Creates a menu for hosting a multiplayer game.
 * Handles the logic for hosting the multiplayer game.
 * @param showMultiplayer Flag to show/hide the multiplayer menu.
 * @param setShowMultiplayer Function to toggle the multiplayer menu.
 * @returns The MultiplayerMenu component.
 */
export default function MultiplayerMenu() {
    const { showMultiplayerMenu, setShowMultiplayerMenu } = useGame();
    const { multiplayerState, hostGame, disconnect, joinGame } = useMultiplayer();

    const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
    const { playClickAudio } = useSoundEffect();

    useEffect(() => {
        document.body.style.backgroundImage = "url('images/table2-4k.webp')";
    });

    useEffect(() => {
        setActiveRow(0);
        if (showMultiplayerMenu && !multiplayerState.waitingForPlayers) {
            const timeout = setTimeout(() => {
                const target = buttonsRef.current[activeRow];
                if (target) target.focus();
            }, 0);
            return () => clearTimeout(timeout);
        }
    }, [showMultiplayerMenu, multiplayerState.waitingForPlayers]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (KeyCodes.Ups.includes(event.key) || KeyCodes.Downs.includes(event.key)) {
                playClickAudio();
            }
        };
        if (showMultiplayerMenu && !multiplayerState.waitingForPlayers) {
            window.addEventListener("keydown", handleKeyDown);
        }
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [showMultiplayerMenu, multiplayerState.waitingForPlayers, playClickAudio]);

    if (!showMultiplayerMenu) return null;

    /* Show the waiting room while waiting for an opponent to join */
    if (multiplayerState.waitingForPlayers) return <WaitingRoom />;

    // TODO - finish this, store games in local storage for a month
    const savedLobby = getSavedLobby();
    const hasSavedGame = savedLobby !== null;

    return (
        <div className="chess-menu mp" data-testid="multiplayer-menu">
            <h1>Multiplayer</h1>
            <ChessButton
                ref={(el) => (buttonsRef.current[0] = el)}
                onClick={(e) => {
                    e.currentTarget.disabled = true;
                    hostGame();
                }}
                data-navcol={0}
                data-navrow={0}
            >
                Host Game
            </ChessButton>
            {hasSavedGame && (
                <div className="rejoin-container">
                    <p>You have a saved game. Do you want to rejoin?</p>
                    <ChessButton
                        ref={(el) => (buttonsRef.current[1] = el)}
                        data-navcol={0}
                        data-navrow={1}
                        onClick={() => {
                            joinGame(savedLobby.lobbyId, savedLobby.username, true);
                        }}
                    >
                        Rejoin
                    </ChessButton>
                </div>
            )}
            <ChessButton
                ref={(el) => (buttonsRef.current[hasSavedGame ? 2 : 1] = el)}
                onClick={() => {
                    setShowMultiplayerMenu(false);
                    disconnect();
                }}
                id="multiplayer-menu-back-button"
                data-navcol={0}
                data-navrow={hasSavedGame ? 2 : 1}
            >
                Back
            </ChessButton>
        </div>
    );
}
